// frontend/admin-portal/src/components/products/CategoryStatsCard.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Button,
  Checkbox,
  Badge,
  Spinner,
  Title,
  SearchSelect,
  formatMoney,
} from "@fiberhome/shared-ui";
import { productService } from "../../services/productService";
import { useSettings } from "../../contexts/SettingsContext";

export default function CategoryStatsCard({ refreshKey = 0 }) {
  const { get } = useSettings();
  const currency = get("finance.currencySymbol", "IQD");

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState({ items: [], total: 0, totalValue: 0 });
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState(""); // SearchSelect value (categoryId or '')
  const [query, setQuery] = useState(""); // free-text filter

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    productService
      .categoryStats({ isActive: true })
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e.response?.data?.message || e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [refreshKey]);

  const sortedItems = useMemo(
    () =>
      [...data.items].sort(
        (a, b) => b.count - a.count || a.name.localeCompare(b.name),
      ),
    [data.items],
  );

  // Options for SearchSelect — a category list, plus a synthetic "All"
  const selectOptions = useMemo(
    () => [
      { value: "", label: `All categories (${sortedItems.length})` },
      ...sortedItems.map((c) => ({
        value: String(c.categoryId),
        label: `${c.name} (${c.count})`,
        // carry meta if SearchSelect supports rendering extra fields
        meta: c,
      })),
    ],
    [sortedItems],
  );

  // Filter the grid by search + query
  const filteredItems = useMemo(() => {
    let list = sortedItems;
    if (search) {
      list = list.filter((c) => String(c.categoryId) === String(search));
    }
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.slug.toLowerCase().includes(q) ||
          c.kind.toLowerCase().includes(q),
      );
    }
    return list;
  }, [sortedItems, search, query]);

  // Selected items always resolve against the full list (not filtered),
  // so hidden-but-selected entries still count.
  const selectedItems = useMemo(
    () => sortedItems.filter((i) => selected.has(i.categoryId)),
    [sortedItems, selected],
  );

  const summary = useMemo(() => {
    const base = selectedItems.length > 0 ? selectedItems : sortedItems;
    return {
      count: base.reduce((s, i) => s + i.count, 0),
      value: base.reduce((s, i) => s + i.totalValue, 0),
      stock: base.reduce((s, i) => s + i.totalStock, 0),
      categories: base.length,
    };
  }, [selectedItems, sortedItems]);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // Select-all operates on the FILTERED set, so "select all match" works
  const visibleIds = filteredItems.map((c) => c.categoryId);
  const visibleSelected = visibleIds.filter((id) => selected.has(id));
  const allVisibleChecked =
    visibleIds.length > 0 && visibleSelected.length === visibleIds.length;
  const someVisibleChecked =
    visibleSelected.length > 0 && visibleSelected.length < visibleIds.length;

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleChecked) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  // Keep the visual highlight in sync with the SearchSelect pick
  const isHighlighted = (id) => search && String(id) === String(search);

  return (
    <Card className="mb-3 fh-catstats">
      {/* Header — same as before */}
      <button
        type="button"
        className="fh-catstats__head"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <div className="fh-catstats__head-left">
          <span
            className={`fh-catstats__chevron ${open ? "is-open" : ""}`}
            aria-hidden="true"
          >
            ▸
          </span>
          <Title level={4} className="mb-0">
            Products by category
          </Title>
          {!loading && (
            <Badge variant="default">
              {filteredItems.length === sortedItems.length
                ? `${sortedItems.length} categories`
                : `${filteredItems.length} / ${sortedItems.length} categories`}{" "}
              · {data.total} products
            </Badge>
          )}
        </div>

        <div className="fh-catstats__head-right">
          {!loading && (
            <>
              <span className="fh-catstats__pill">
                <span className="text-muted">Count</span>
                <strong>{summary.count}</strong>
              </span>
              <span className="fh-catstats__pill">
                <span className="text-muted">Stock</span>
                <strong>{summary.stock}</strong>
              </span>
              <span className="fh-catstats__pill">
                <span className="text-muted">Value</span>
                <strong>
                  {formatMoney(summary.value, { symbol: currency })}
                </strong>
              </span>
              {selected.size > 0 && (
                <Badge variant="primary">{selected.size} selected</Badge>
              )}
            </>
          )}
        </div>
      </button>

      {open && (
        <div className="fh-catstats__body">
          {error && <div className="fh-alert fh-alert--danger">{error}</div>}

          {loading ? (
            <div className="py-4 text-center">
              <Spinner size="md" />
            </div>
          ) : sortedItems.length === 0 ? (
            <div className="text-muted py-3">No categories yet.</div>
          ) : (
            <>
              {/* Filter bar */}
              <div className="fh-catstats__filterbar">
                <div className="fh-catstats__searchselect">
                  <SearchSelect
                    options={selectOptions}
                    value={search}
                    onChange={(optOrValue) => {
                      // support both {value} objects and raw value strings
                      const v =
                        typeof optOrValue === "object" && optOrValue !== null
                          ? optOrValue.value
                          : optOrValue;
                      setSearch(v ?? "");
                    }}
                    placeholder="Jump to category…"
                    isClearable
                  />
                </div>

                <div className="fh-catstats__query">
                  <input
                    type="search"
                    className="fh-input fh-input--compact"
                    placeholder="Filter by name, slug or kind…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>

                {(search || query) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setSearch("");
                      setQuery("");
                    }}
                  >
                    Reset filters
                  </Button>
                )}
              </div>

              {/* Selection toolbar */}
              <div className="fh-catstats__toolbar">
                <label className="fh-catstats__selectall">
                  <Checkbox
                    checked={allVisibleChecked}
                    indeterminate={someVisibleChecked}
                    onChange={toggleAllVisible}
                    ariaLabel="Select all visible categories"
                  />
                  <span>
                    Select {search || query ? "visible" : "all"} (
                    {visibleIds.length})
                  </span>
                </label>
                {selected.size > 0 && (
                  <Button size="sm" variant="ghost" onClick={clearSelection}>
                    Clear selection ({selected.size})
                  </Button>
                )}
              </div>

              {/* Grid */}
              {filteredItems.length === 0 ? (
                <div className="text-muted py-3">
                  No categories match your filters.
                </div>
              ) : (
                <div className="fh-catstats__grid">
                  {filteredItems.map((cat) => {
                    const isSel = selected.has(cat.categoryId);
                    return (
                      <label
                        key={cat.categoryId}
                        className={
                          "fh-catstats__row" +
                          (isSel ? " is-selected" : "") +
                          (isHighlighted(cat.categoryId)
                            ? " is-highlighted"
                            : "")
                        }
                      >
                        <Checkbox
                          checked={isSel}
                          onChange={() => toggle(cat.categoryId)}
                          ariaLabel={`Select ${cat.name}`}
                        />
                        <div className="fh-catstats__row-main">
                          <div className="fh-catstats__row-title">
                            <span>{cat.name}</span>
                            <Badge
                              variant={cat.count > 0 ? "success" : "default"}
                            >
                              {cat.count}
                            </Badge>
                          </div>
                          <div className="fh-catstats__row-meta">
                            <span className="text-muted">{cat.kind}</span>
                            <span className="text-muted">
                              stock {cat.totalStock}
                            </span>
                            <span className="text-muted">
                              {formatMoney(cat.totalValue, {
                                symbol: currency,
                              })}
                            </span>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Summary */}
              {selected.size > 0 && (
                <div className="fh-catstats__summary">
                  <div>
                    <strong>{selectedItems.length}</strong> categories selected
                    · <strong>{summary.count}</strong> products ·{" "}
                    <strong>{summary.stock}</strong> units ·{" "}
                    <strong>
                      {formatMoney(summary.value, { symbol: currency })}
                    </strong>
                  </div>
                  <div className="fh-catstats__summary-chips">
                    {selectedItems.map((c) => (
                      <button
                        key={c.categoryId}
                        className="fh-catstats__chip"
                        onClick={() => toggle(c.categoryId)}
                        title="Remove"
                      >
                        {c.name} · {c.count} ×
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}
